import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { withLoadingReducer } from 'utils/reducerHandler'
import { GetPurchaseOrderReferencesResponseType } from 'features/PurchaseOrder/services/references'
import {
  GetSOProductsDirectRequestType,
  GetSOProductsDirectResponseType,
  GetSupplyOrderRemarksResponseType,
} from 'features/SupplyOrder/services/outboundSO'
import { GetAllLocationsResponseType } from 'features/Location/services/location'
import { GetProductTypeResponseType } from 'features/Product/services/product'
import { InventoryStatusNotesItemType } from 'features/Product/@types/Product'
import {
  fetchGetSOProductDirect,
  fetchPostSupplyOrderDetail,
  fetchPutSupplyOrderDetail,
  fetchGetProductType,
  fetchGetInventoryStatusNotes,
  SLICE_NAME,
} from './thunk'

type ProductItemType = {
  productId: number
  productQrCode: string
  productRequestQuantity: number
  productStatusId: number
  productStatusNoteId: number | null
  productStock: number
}

export type OutboundSOAddProductDirectSliceType = {
  isLoading: boolean
  checkedIds: string[]
  selectedItems: Record<string, ProductItemType>
  query: GetSOProductsDirectRequestType
  data: (GetSOProductsDirectResponseType['data'][number] & {
    productUniqueKey: string
  })[]
  createSO: {
    poReff?: Nullable<GetPurchaseOrderReferencesResponseType['data']['content'][number]>
    soReff: string
    soType: string
    soDate: Date | null
    shippingInstructions: string
    email: string
    remarks: Nullable<GetSupplyOrderRemarksResponseType['data'][number]>
    origin: Nullable<GetAllLocationsResponseType[number]>
    destination: Nullable<GetAllLocationsResponseType[number]>
    createdBy?: string
    status?: string
    supplyOrderId?: number
  }
  constants: {
    productType: GetProductTypeResponseType['data']
    statusNotes: InventoryStatusNotesItemType[]
  }
}

const initialState: OutboundSOAddProductDirectSliceType = {
  isLoading: false,
  checkedIds: [],
  selectedItems: {},
  query: {
    pageIndex: 1,
    pageSize: 20,
  },
  data: [],
  createSO: {
    poReff: null,
    soReff: '',
    soType: '',
    soDate: null,
    shippingInstructions: '',
    email: '',
    remarks: null,
    origin: null,
    destination: null,
  },
  constants: {
    productType: [],
    statusNotes: [],
  },
}

const outboundSOAddProductDirectSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    setCreateSO: (
      state,
      { payload }: PayloadAction<OutboundSOAddProductDirectSliceType['createSO']>,
    ) => {
      state.createSO = payload
    },
    setCheckItem: (state, { payload }: PayloadAction<string>) => {
      const index = state.checkedIds.indexOf(payload)

      if (index >= 0) {
        state.checkedIds.splice(index, 1)
        delete state.selectedItems[payload]
      } else {
        state.checkedIds.push(payload)
      }
    },
    setCheckAll: (state, { payload }: PayloadAction<string[]>) => {
      state.checkedIds = Array.from(new Set([...state.checkedIds, ...payload]))
    },
    setUncheckAll: (state, { payload }: PayloadAction<string[]>) => {
      state.checkedIds = state.checkedIds.filter((id) => !payload.includes(id))
    },
    setItemQty: (
      state,
      { payload }: PayloadAction<{ productUniqueKey: string; item: ProductItemType }>,
    ) => {
      const { productUniqueKey, item } = payload

      if (item.productRequestQuantity) {
        state.selectedItems[productUniqueKey] = item
        if (!state.checkedIds.includes(productUniqueKey)) {
          state.checkedIds.push(productUniqueKey)
        }
      } else {
        delete state.selectedItems[productUniqueKey]
        state.checkedIds = state.checkedIds.filter((id) => id !== productUniqueKey)
      }
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<OutboundSOAddProductDirectSliceType>) => {
      builder.addCase(fetchGetSOProductDirect.pending, (state, action) => {
        state.query = {
          ...state.query,
          ...action.meta.arg,
        }
      })
      builder.addCase(fetchGetSOProductDirect.fulfilled, (state, { payload }) => {
        state.data = payload.data.map((item) => ({
          ...item,
          productUniqueKey: `${item.productId}-${item.productInventoryStatusId}-${item.productInventoryStatusNoteId}`,
        }))
      })
      builder.addCase(fetchGetProductType.fulfilled, (state, { payload }) => {
        state.constants.productType = payload
      })
      builder.addCase(fetchGetInventoryStatusNotes.fulfilled, (state, { payload }) => {
        state.constants.statusNotes = payload
      })
    },
    [fetchGetSOProductDirect, fetchPutSupplyOrderDetail, fetchPostSupplyOrderDetail],
  ),
})

export const { reset, setCreateSO, setCheckItem, setCheckAll, setUncheckAll, setItemQty } =
  outboundSOAddProductDirectSlice.actions
export default outboundSOAddProductDirectSlice.reducer
